<template>
  <div class="weather-management">
    <div class="page-header">
      <h1>天气 Key 管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="handleAdd">
          <el-icon><Plus /></el-icon>
          添加配置
        </el-button>
        <el-button @click="loadConfigs">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 天气配置列表 -->
    <el-table
      v-loading="loading"
      :data="weatherConfigs"
      stripe
      style="width: 100%"
      empty-text="暂无天气配置"
    >
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="api_provider" label="API提供商" width="120">
        <template #default="{ row }">
          <el-tag>{{ row.api_provider || 'seniverse' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="api_key" label="API Key" min-width="200">
        <template #default="{ row }">
          <span v-if="showKeys[row.id]">{{ row.api_key }}</span>
          <span v-else>••••••••••••••••</span>
          <el-button
            type="text"
            size="small"
            @click="toggleKey(row.id)"
            style="margin-left: 8px;"
          >
            {{ showKeys[row.id] ? '隐藏' : '显示' }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
      <el-table-column prop="is_active" label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.is_active ? 'success' : 'info'">
            {{ row.is_active ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="180" />
      <el-table-column prop="updated_at" label="更新时间" width="180" />
      <el-table-column label="操作" width="250" fixed="right">
        <template #default="{ row }">
          <el-button 
            type="success" 
            link 
            size="small" 
            @click="handleTest(row)"
            :loading="testing[row.id]"
          >
            <el-icon><VideoPlay /></el-icon>
            测试
          </el-button>
          <el-button type="primary" link size="small" @click="handleEdit(row)">
            编辑
          </el-button>
          <el-button type="danger" link size="small" @click="handleDelete(row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="showEditDialog"
      :title="editMode ? '编辑天气配置' : '添加天气配置'"
      width="600px"
      @close="resetForm"
    >
      <el-form
        ref="weatherFormRef"
        :model="weatherForm"
        :rules="weatherRules"
        label-width="120px"
      >
        <el-form-item label="API Key" prop="api_key">
          <el-input
            v-model="weatherForm.api_key"
            placeholder="请输入天气 API Key"
            type="password"
            show-password
          />
        </el-form-item>
        <el-form-item label="API提供商" prop="api_provider">
          <el-select v-model="weatherForm.api_provider" placeholder="请选择API提供商" style="width: 100%;">
            <el-option label="心知天气 (seniverse)" value="seniverse" />
            <el-option label="其他" value="other" />
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="weatherForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入配置描述（可选）"
          />
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch
            v-model="weatherForm.is_active"
            :active-value="1"
            :inactive-value="0"
          />
          <span style="margin-left: 10px; color: #909399; font-size: 12px;">
            启用后，其他配置将自动禁用
          </span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Refresh, VideoPlay } from '@element-plus/icons-vue';
import {
  getWeatherConfigs,
  createWeatherConfig,
  updateWeatherConfig,
  deleteWeatherConfig,
  testWeatherAPI
} from '../api/weather';

export default {
  name: 'WeatherManagement',
  components: {
    Plus,
    Refresh,
    VideoPlay
  },
  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const testing = ref({}); // 改为对象，记录每个配置的测试状态
    const weatherConfigs = ref([]);
    const showEditDialog = ref(false);
    const editMode = ref(false);
    const weatherFormRef = ref(null);
    const showKeys = ref({});

    const weatherForm = reactive({
      id: null,
      api_key: '',
      api_provider: 'seniverse',
      description: '',
      is_active: 1
    });

    const weatherRules = {
      api_key: [
        { required: true, message: '请输入 API Key', trigger: 'blur' }
      ],
      api_provider: [
        { required: true, message: '请选择 API 提供商', trigger: 'change' }
      ]
    };

    // 加载配置列表
    const loadConfigs = async () => {
      try {
        loading.value = true;
        const response = await getWeatherConfigs();
        if (response.success) {
          weatherConfigs.value = response.data || [];
        } else {
          ElMessage.error(response.message || '加载配置列表失败');
        }
      } catch (error) {
        console.error('加载配置列表失败:', error);
        ElMessage.error('加载配置列表失败');
      } finally {
        loading.value = false;
      }
    };

    // 切换显示/隐藏 Key
    const toggleKey = (id) => {
      showKeys.value[id] = !showKeys.value[id];
    };

    // 添加配置
    const handleAdd = () => {
      editMode.value = false;
      resetForm();
      showEditDialog.value = true;
    };

    // 编辑配置
    const handleEdit = (row) => {
      editMode.value = true;
      Object.assign(weatherForm, {
        id: row.id,
        api_key: row.api_key,
        api_provider: row.api_provider || 'seniverse',
        description: row.description || '',
        is_active: row.is_active
      });
      showEditDialog.value = true;
    };

    // 删除配置
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除该天气配置吗？`,
          '提示',
          {
            type: 'warning'
          }
        );
        
        const response = await deleteWeatherConfig(row.id);
        if (response.success) {
          ElMessage.success('删除成功');
          loadConfigs();
        } else {
          ElMessage.error(response.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除配置失败:', error);
          ElMessage.error('删除配置失败');
        }
      }
    };

    // 保存配置
    const handleSave = async () => {
      try {
        await weatherFormRef.value.validate();
        
        saving.value = true;
        const response = editMode.value
          ? await updateWeatherConfig(weatherForm.id, weatherForm)
          : await createWeatherConfig(weatherForm);
        
        if (response.success) {
          ElMessage.success(editMode.value ? '更新成功' : '创建成功');
          showEditDialog.value = false;
          loadConfigs();
        } else {
          ElMessage.error(response.message || (editMode.value ? '更新失败' : '创建失败'));
        }
      } catch (error) {
        if (error !== false) {
          console.error('保存配置失败:', error);
          ElMessage.error('保存配置失败');
        }
      } finally {
        saving.value = false;
      }
    };

    // 重置表单
    const resetForm = () => {
      Object.assign(weatherForm, {
        id: null,
        api_key: '',
        api_provider: 'seniverse',
        description: '',
        is_active: 1
      });
      weatherFormRef.value?.resetFields();
    };

    // 测试天气 API（针对特定配置）
    const handleTest = async (row) => {
      try {
        testing.value[row.id] = true;
        // 传入配置ID，测试指定的配置
        const response = await testWeatherAPI(row.id);
        
        if (response && response.success) {
          const data = response.data || {};
          const message = response.message || '测试成功';
          
          ElMessage.success({
            message: message,
            duration: 5000,
            showClose: true
          });
          
          // 如果有详细信息，可以显示更多
          if (data.location && data.weather && data.temperature) {
            console.log('测试结果详情:', data);
          }
        } else {
          // 如果响应存在但没有成功，显示后端返回的错误信息
          const errorMsg = (response && response.message) ? response.message : '测试失败';
          ElMessage.error(errorMsg);
        }
      } catch (error) {
        console.error('测试天气 API 失败:', error);
        
        // 尝试从错误响应中获取详细错误信息
        let errorMessage = '测试失败，请检查网络连接或 API 配置';
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        ElMessage.error(errorMessage);
      } finally {
        testing.value[row.id] = false;
      }
    };

    onMounted(() => {
      loadConfigs();
    });

    return {
      loading,
      saving,
      testing,
      weatherConfigs,
      showEditDialog,
      editMode,
      weatherFormRef,
      weatherForm,
      weatherRules,
      showKeys,
      loadConfigs,
      toggleKey,
      handleAdd,
      handleEdit,
      handleDelete,
      handleSave,
      handleTest,
      resetForm
    };
  }
};
</script>

<style scoped>
.weather-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
}
</style>

